import { getSeatCategory, isSeat, isSeatOccupied } from './seats';
import { POSITION_ORDER } from './seatPreferenceConstants';

const PADDED_ROW_WIDTH = 7;
const PADDED_SEAT_SLOTS = [0, 2, 4, 6];
const COMPACT_ROW_WIDTH = 5;
const BUS_TOPOLOGIES = Object.freeze({
  padded: Object.freeze({
    width: PADDED_ROW_WIDTH,
    centerAisle: 3,
    sides: Object.freeze([
      Object.freeze({ slots: Object.freeze([0, 2]), window: 0, aisle: 2, spacer: 1 }),
      Object.freeze({ slots: Object.freeze([4, 6]), window: 6, aisle: 4, spacer: 5 }),
    ]),
  }),
  compact: Object.freeze({
    width: COMPACT_ROW_WIDTH,
    centerAisle: 2,
    sides: Object.freeze([
      Object.freeze({ slots: Object.freeze([0, 1]), window: 0, aisle: 1 }),
      Object.freeze({ slots: Object.freeze([3, 4]), window: 4, aisle: 3 }),
    ]),
  }),
});
const FLOOR_MISSING = 'missing';
const FLOOR_VALID = 'valid';
const FLOOR_INVALID = 'invalid';

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * Normalizes a seat number without collapsing leading zeroes or alphanumeric values.
 * @param {*} value Raw API value.
 * @returns {string|null} Canonical number used for identity comparisons.
 */
export const normalizeSeatNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  if (typeof value !== 'string') return null;

  const normalized = value.trim();
  return normalized || null;
};

/**
 * Parses reservation floor metadata while keeping missing and malformed values distinct.
 * @param {*} value Raw seatLevel-compatible value.
 * @returns {{status: 'missing'|'valid'|'invalid', value: string|null}} Parse result.
 */
export const parseSeatFloor = (value) => {
  if (value === undefined || value === null || value === '') {
    return { status: FLOOR_MISSING, value: null };
  }

  if (typeof value === 'number') {
    return Number.isInteger(value) && value > 0
      ? { status: FLOOR_VALID, value: String(value) }
      : { status: FLOOR_INVALID, value: null };
  }

  if (typeof value !== 'string' || !/^\d+$/.test(value.trim())) {
    return { status: FLOOR_INVALID, value: null };
  }

  const numericFloor = Number(value.trim());
  return Number.isSafeInteger(numericFloor) && numericFloor > 0
    ? { status: FLOOR_VALID, value: String(numericFloor) }
    : { status: FLOOR_INVALID, value: null };
};

const cloneValue = (value, seen = new WeakMap()) => {
  if (!value || typeof value !== 'object') return value;
  if (seen.has(value)) return seen.get(value);

  const clone = Array.isArray(value) ? [] : {};
  seen.set(value, clone);
  Object.keys(value).forEach((key) => {
    clone[key] = cloneValue(value[key], seen);
  });
  return clone;
};

const valuesMatch = (left, right, seen = new WeakMap()) => {
  if (left === right) return true;
  if (!left || !right || typeof left !== 'object' || typeof right !== 'object') return false;
  if (Array.isArray(left) !== Array.isArray(right)) return false;
  if (seen.get(left) === right) return true;
  seen.set(left, right);

  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  if (leftKeys.length !== rightKeys.length) return false;

  return leftKeys.every(
    (key, index) => key === rightKeys[index] && valuesMatch(left[key], right[key], seen),
  );
};

const isNumberedSpace = (space) => isObject(space) && normalizeSeatNumber(space.number) !== null;
const isSeatSpace = (space) => isNumberedSpace(space) && isSeat(getSeatCategory(space));
const isOrdinarySeatSpace = (space) =>
  isSeatSpace(space) && getSeatCategory(space) !== 'hallway_seat';
const isHallwaySpacer = (space) =>
  isObject(space) &&
  normalizeSeatNumber(space.number) === null &&
  getSeatCategory(space) === 'hallway';
const isPaddedRow = (row) =>
  Array.isArray(row) && row.length === PADDED_ROW_WIDTH && Array.from(row).every(isObject);
const isCompactRow = (row) =>
  Array.isArray(row) && row.length === COMPACT_ROW_WIDTH && Array.from(row).every(isObject);
const coordinateKey = (id) => JSON.stringify(id);
const createReservationKey = (tripSlug, floor, number) =>
  JSON.stringify([tripSlug === undefined ? null : tripSlug, floor, number]);

const canonicalPositions = (positions) => {
  if (!positions || positions.size === 0) return null;
  return POSITION_ORDER.filter((position) => positions.has(position));
};

const getLayoutShape = (layout) => {
  if (!Array.isArray(layout)) return { decks: [], isThreeDimensional: false };

  const isThreeDimensional =
    layout.length > 0 &&
    layout.every((deck) => Array.isArray(deck) && deck.every((row) => Array.isArray(row)));
  if (isThreeDimensional) return { decks: layout, isThreeDimensional: true };

  const isTwoDimensional = layout.every(Array.isArray);
  if (isTwoDimensional) return { decks: [layout], isThreeDimensional: false };

  return { decks: [[layout]], isThreeDimensional: false };
};

const getDeckFloor = (deck, deckIndex) => {
  const ordinalFloor = deckIndex < 2 ? String(deckIndex + 1) : null;
  const explicitFloors = new Set();

  deck.forEach((row) => {
    row.forEach((space) => {
      if (!isNumberedSpace(space)) return;
      const parsed = parseSeatFloor(space.seatLevel);
      if (parsed.status === FLOOR_VALID) explicitFloors.add(parsed.value);
    });
  });

  if (explicitFloors.size > 1) return { floor: null, coherent: false };
  const [explicitFloor] = explicitFloors;
  if (explicitFloor && ordinalFloor && explicitFloor !== ordinalFloor) {
    return { floor: null, coherent: false };
  }

  return { floor: explicitFloor || ordinalFloor, coherent: true };
};

const getDescriptorFloor = ({ isThreeDimensional, parsedFloor, deckIdentity }) => {
  if (isThreeDimensional) {
    if (parsedFloor.status === FLOOR_INVALID || !deckIdentity.coherent) return null;
    return deckIdentity.floor;
  }
  return parsedFloor.status === FLOOR_VALID ? parsedFloor.value : null;
};

const addNeighbor = (recordsByCoordinate, leftRecord, rightRecord) => {
  if (!leftRecord || !rightRecord) return;

  const leftKey = coordinateKey(leftRecord.id);
  const rightKey = coordinateKey(rightRecord.id);
  if (!recordsByCoordinate.has(leftKey) || !recordsByCoordinate.has(rightKey)) return;
  leftRecord.neighborKeys.add(rightKey);
  rightRecord.neighborKeys.add(leftKey);
};

const hasSeatSpace = (row) => Array.from(row).some(isSeatSpace);

const compactRowPreservesAisle = (row) =>
  isCompactRow(row) && (!hasSeatSpace(row) || isHallwaySpacer(row[2]));

const getBusTopology = (decks) => {
  const rows = Array.from(decks).flatMap((deck) => Array.from(deck));
  if (rows.length === 0) return null;
  if (rows.every(isPaddedRow)) return BUS_TOPOLOGIES.padded;
  if (rows.every(compactRowPreservesAisle)) return BUS_TOPOLOGIES.compact;
  return null;
};

const getDemonstratedSingleSideSlots = (deck, topology) =>
  topology.sides.map(({ slots }) => {
    const seatBearingRows = deck.filter((row) => {
      return slots.some((slot) => isOrdinarySeatSpace(row[slot]));
    });
    if (seatBearingRows.length < 2) return null;

    const occupiedSlots = seatBearingRows.map((row) => {
      return slots.filter((slot) => isOrdinarySeatSpace(row[slot]));
    });
    if (occupiedSlots.some((rowSlots) => rowSlots.length !== 1)) return null;

    const [[singleSlot]] = occupiedSlots;
    const isConsistent = occupiedSlots.every(([slot]) => slot === singleSlot);
    const competingSlotsAreHallways = seatBearingRows.every((row) => {
      return slots.every((slot) => slot === singleSlot || isHallwaySpacer(row[slot]));
    });
    const aislePathIsClear = seatBearingRows.every((row) => {
      const path =
        singleSlot < topology.centerAisle
          ? row.slice(singleSlot + 1, topology.centerAisle + 1)
          : row.slice(topology.centerAisle, singleSlot);
      return path.every(isHallwaySpacer);
    });
    return isConsistent && competingSlotsAreHallways && aislePathIsClear ? singleSlot : null;
  });

const applyBusGeometry = (row, rowRecords, recordsByCoordinate, topology, singleSideSlots) => {
  topology.sides.forEach(({ slots, window, aisle, spacer }, sideIndex) => {
    const windowRecord = rowRecords.get(window);
    if (windowRecord && isOrdinarySeatSpace(row[window])) {
      windowRecord.positions.add('window');
    }

    if (isHallwaySpacer(row[topology.centerAisle])) {
      const aisleRecord = rowRecords.get(aisle);
      if (aisleRecord && isOrdinarySeatSpace(row[aisle])) aisleRecord.positions.add('aisle');
    }

    const singleSlot = singleSideSlots[sideIndex];
    const singleRecord = singleSlot === null ? null : rowRecords.get(singleSlot);
    if (singleRecord && isOrdinarySeatSpace(row[singleSlot])) {
      singleRecord.positions.add('window');
      singleRecord.positions.add('aisle');
    }

    const [left, right] = slots;
    const cellsAreAdjacent = spacer === undefined ? true : isHallwaySpacer(row[spacer]);
    if (cellsAreAdjacent && isOrdinarySeatSpace(row[left]) && isOrdinarySeatSpace(row[right])) {
      addNeighbor(recordsByCoordinate, rowRecords.get(left), rowRecords.get(right));
    }
  });
};

const applyVanGeometry = (row, rowRecords, recordsByCoordinate, isFinalRow) => {
  if (!isPaddedRow(row)) return;

  const isCompleteRearBench =
    isFinalRow &&
    PADDED_SEAT_SLOTS.every((slot) => isOrdinarySeatSpace(row[slot])) &&
    [1, 3, 5].every((slot) => isHallwaySpacer(row[slot]));

  if (isCompleteRearBench) {
    [0, 6].forEach((slot) => rowRecords.get(slot).positions.add('window'));
    [
      [0, 1, 2],
      [2, 3, 4],
      [4, 5, 6],
    ].forEach(([left, spacer, right]) => {
      if (isHallwaySpacer(row[spacer])) {
        addNeighbor(recordsByCoordinate, rowRecords.get(left), rowRecords.get(right));
      }
    });
    return;
  }

  if (isFinalRow) return;

  const leftWindow = rowRecords.get(0);
  if (leftWindow && isOrdinarySeatSpace(row[0])) leftWindow.positions.add('window');

  if (isHallwaySpacer(row[5])) {
    const aisleSeat = rowRecords.get(4);
    const singleSeat = rowRecords.get(6);
    if (aisleSeat && isOrdinarySeatSpace(row[4])) aisleSeat.positions.add('aisle');
    if (singleSeat && isOrdinarySeatSpace(row[6])) {
      singleSeat.positions.add('window');
      singleSeat.positions.add('aisle');
    }
  }

  [
    [0, 1, 2],
    [2, 3, 4],
  ].forEach(([left, spacer, right]) => {
    if (
      isOrdinarySeatSpace(row[left]) &&
      isHallwaySpacer(row[spacer]) &&
      isOrdinarySeatSpace(row[right])
    ) {
      addNeighbor(recordsByCoordinate, rowRecords.get(left), rowRecords.get(right));
    }
  });
};

const neighborEvidence = (record, recordsByCoordinate) =>
  Array.from(record.neighborKeys)
    .map((key) => {
      const neighbor = recordsByCoordinate.get(key);
      return neighbor && neighbor.reservationKey ? neighbor.reservationKey : key;
    })
    .sort();

const finalizeDuplicateIdentities = (records, recordsByCoordinate) => {
  const groups = new Map();

  records.forEach((record) => {
    if (!record.reservationKey || !record.preference.floor) return;
    const group = groups.get(record.reservationKey) || [];
    group.push(record);
    groups.set(record.reservationKey, group);
  });

  const discarded = new Set();
  const remapped = new Map();
  const conflicting = new Set();

  groups.forEach((group) => {
    if (group.length < 2) return;
    const [first] = group;
    const equivalent = group
      .slice(1)
      .every(
        (record) =>
          valuesMatch(first.rawSeat, record.rawSeat) &&
          valuesMatch(canonicalPositions(first.positions), canonicalPositions(record.positions)) &&
          valuesMatch(
            neighborEvidence(first, recordsByCoordinate),
            neighborEvidence(record, recordsByCoordinate),
          ),
      );

    if (equivalent) {
      group.slice(1).forEach((record) => {
        const key = coordinateKey(record.id);
        discarded.add(key);
        remapped.set(key, coordinateKey(first.id));
      });
      return;
    }

    group.forEach((record) => {
      conflicting.add(coordinateKey(record.id));
      record.reservationKey = null;
      record.neighborKeys.clear();
    });
  });

  records.forEach((record) => {
    record.neighborKeys.forEach((key) => {
      if (conflicting.has(key)) record.neighborKeys.delete(key);
    });
  });

  const retained = records.filter((record) => !discarded.has(coordinateKey(record.id)));
  const retainedByCoordinate = new Map(
    retained.map((record) => [coordinateKey(record.id), record]),
  );

  retained.forEach((record) => {
    const ownKey = coordinateKey(record.id);
    const normalizedNeighbors = new Set();
    record.neighborKeys.forEach((key) => {
      const mappedKey = remapped.get(key) || key;
      if (mappedKey !== ownKey && retainedByCoordinate.has(mappedKey)) {
        normalizedNeighbors.add(mappedKey);
      }
    });
    record.neighborKeys = normalizedNeighbors;
  });

  return { records: retained, recordsByCoordinate: retainedByCoordinate };
};

const publicDescriptor = (record, recordsByCoordinate) => ({
  seat: cloneValue(record.seat),
  id: [...record.id],
  reservationKey: record.reservationKey,
  preference: {
    ...record.preference,
    position: canonicalPositions(record.positions),
  },
  order: record.order,
  neighbors: Array.from(record.neighborKeys).map((key) => [...recordsByCoordinate.get(key).id]),
});

/**
 * Describes every numbered raw layout object without using the collision-prone getSeats map.
 * Geometry requires one supported row topology across the entire three-dimensional bus/van
 * layout, independently of uniform rendering. Compact geometry is bus-only and requires
 * its center aisle on seat-bearing rows.
 * @param {Array} layout Raw seat layout.
 * @param {Object} [options] Descriptor options.
 * @param {string} [options.tripSlug] Trip identity.
 * @param {string} [options.diagramType] Geometry family. Omitted values remain unknown.
 * @param {boolean} [options.showUniformLayout=false] Whether uniform rendering is active.
 * @returns {Array<Object>} Ordered collision-safe seat descriptors.
 */
export const describeSeatLayout = (layout, options = {}) => {
  const { tripSlug, diagramType } = options;
  const { decks, isThreeDimensional } = getLayoutShape(layout);
  const records = [];
  const recordsByCoordinate = new Map();
  let order = 0;
  let legacyIndex = 0;

  decks.forEach((deck, deckIndex) => {
    if (!Array.isArray(deck)) return;
    const deckIdentity = isThreeDimensional
      ? getDeckFloor(deck, deckIndex)
      : { floor: null, coherent: true };

    deck.forEach((row, rowIndex) => {
      if (!Array.isArray(row)) return;
      row.forEach((space, spaceIndex) => {
        if (isObject(space) && space.number) legacyIndex += 1;
        if (!isNumberedSpace(space)) return;

        order += 1;
        const number = normalizeSeatNumber(space.number);
        const parsedFloor = parseSeatFloor(space.seatLevel);
        const floor = getDescriptorFloor({ isThreeDimensional, parsedFloor, deckIdentity });
        const identityUsable = parsedFloor.status !== FLOOR_INVALID && deckIdentity.coherent;
        const id = [tripSlug, deckIndex, rowIndex, spaceIndex];
        const seat = cloneValue(space);
        seat.tripSlug = tripSlug;
        seat.index = space.number ? legacyIndex : undefined;
        const record = {
          rawSeat: space,
          seat,
          id,
          reservationKey: identityUsable ? createReservationKey(tripSlug, floor, number) : null,
          preference: { number, floor, position: null },
          positions: new Set(),
          neighborKeys: new Set(),
          order,
        };

        records.push(record);
        recordsByCoordinate.set(coordinateKey(id), record);
      });
    });
  });

  const allDecksAreArrays = Array.from(decks).every(Array.isArray);
  const busTopology =
    isThreeDimensional && diagramType === 'bus' && allDecksAreArrays ? getBusTopology(decks) : null;
  const hasSupportedGeometry =
    isThreeDimensional &&
    allDecksAreArrays &&
    ((diagramType === 'bus' && busTopology) ||
      (diagramType === 'van' && Array.from(decks).every((deck) => deck.every(isPaddedRow))));
  if (hasSupportedGeometry) {
    decks.forEach((deck, deckIndex) => {
      const singleSideSlots =
        diagramType === 'bus' ? getDemonstratedSingleSideSlots(deck, busTopology) : null;
      deck.forEach((row, rowIndex) => {
        const rowRecords = new Map();
        row.forEach((space, spaceIndex) => {
          if (!isSeatSpace(space)) return;
          const record = recordsByCoordinate.get(
            coordinateKey([tripSlug, deckIndex, rowIndex, spaceIndex]),
          );
          if (record) rowRecords.set(spaceIndex, record);
        });

        if (diagramType === 'van') {
          applyVanGeometry(row, rowRecords, recordsByCoordinate, rowIndex === deck.length - 1);
        } else {
          applyBusGeometry(row, rowRecords, recordsByCoordinate, busTopology, singleSideSlots);
        }
      });
    });
  }

  const finalized = finalizeDuplicateIdentities(records, recordsByCoordinate);
  return finalized.records.map((record) => publicDescriptor(record, finalized.recordsByCoordinate));
};

const cloneDescriptor = (descriptor) => ({
  seat: cloneValue(descriptor.seat),
  id: [...descriptor.id],
  reservationKey: descriptor.reservationKey,
  preference: {
    ...descriptor.preference,
    position: descriptor.preference.position ? [...descriptor.preference.position] : null,
  },
  order: descriptor.order,
  neighbors: descriptor.neighbors.map((id) => [...id]),
});

const isAvailable = (descriptor) =>
  getSeatCategory(descriptor.seat) !== 'hallway' &&
  !descriptor.seat.petFriendly &&
  !isSeatOccupied(descriptor.seat);

/**
 * Resolves an entire ordered reference set against current physical descriptors.
 * @param {Array<Object>} descriptors Output from describeSeatLayout.
 * @param {Array<Object>} references Ordered raw seat references.
 * @param {Object} [options] Resolution options.
 * @param {boolean} [options.requireAvailable=false] Require current legacy availability.
 * @returns {Array<Object>|null} Ordered cloned descriptors, or null for any partial/ambiguous set.
 */
export const resolveSelectedSeatReferences = (
  descriptors,
  references,
  { requireAvailable = false } = {},
) => {
  if (!Array.isArray(descriptors) || !Array.isArray(references)) return null;

  const usedIdentities = new Set();
  const resolved = [];

  const allReferencesResolved = Array.from(references).every((reference) => {
    if (!isObject(reference)) return false;

    const number = normalizeSeatNumber(reference.number);
    if (number === null) return false;
    const parsedFloor = parseSeatFloor(reference.seatLevel);
    if (parsedFloor.status === FLOOR_INVALID) return false;

    const sameNumber = descriptors.filter((descriptor) => {
      if (!descriptor || descriptor.preference?.number !== number) return false;
      if (
        Object.prototype.hasOwnProperty.call(reference, 'tripSlug') &&
        reference.tripSlug !== undefined &&
        reference.tripSlug !== null &&
        descriptor.id?.[0] !== reference.tripSlug
      ) {
        return false;
      }
      return true;
    });
    const candidates =
      parsedFloor.status === FLOOR_VALID
        ? sameNumber.filter((descriptor) => descriptor.preference.floor === parsedFloor.value)
        : sameNumber;

    if (candidates.length !== 1) return false;
    const [candidate] = candidates;
    if (!candidate.reservationKey) return false;
    if (requireAvailable && !isAvailable(candidate)) return false;
    if (usedIdentities.has(candidate.reservationKey)) return false;

    usedIdentities.add(candidate.reservationKey);
    resolved.push(cloneDescriptor(candidate));
    return true;
  });

  return allReferencesResolved ? resolved : null;
};

export default describeSeatLayout;
